import NextLink, { type LinkProps as NextLinkProps } from 'next/link';
import {
  createElement,
  type CSSProperties,
  type HTMLAttributeAnchorTarget,
} from 'react';

interface LinkProps extends Omit<NextLinkProps, 'href'> {
  children: React.ReactNode;
  href?: string | null;
  className?: string;
  fallback?: React.ElementType;
  style?: CSSProperties;
  rel?: string;
  target?: HTMLAttributeAnchorTarget;
  useSiteColor?: boolean;
  forceReload?: boolean;
  allowRelative?: boolean;
  title?: string;
}

export const Link = ({
  children,
  href,
  className,
  fallback = 'span',
  style,
  useSiteColor = false,
  forceReload = false,
  allowRelative = false,
  ...props
}: LinkProps) => {
  // If there is no href, render a span or other fallback element
  if (!href) return createElement(fallback, { className }, children);

  // Use the site color for links if specified
  if (useSiteColor) {
    style = {
      ...style,
      color:
        'var(--site-link-color, var(--site-primary-color, --on3-primary-color))',
    };
  }

  // If the href is relative, make it absolute
  if (!href.startsWith('/') && !href.startsWith('http') && !allowRelative) {
    href = `/${href}`;
  }

  // If the href is an external link, open it in a new tab
  if (
    href.startsWith('http') &&
    !href.includes('on3.com') &&
    !href.includes('on3.local') // Allow local links
  ) {
    return (
      <a
        className={className}
        href={href}
        rel="noopener noreferrer"
        style={style}
        target="_blank"
        {...props}
      >
        {children}
      </a>
    );
  }

  if (forceReload || href.startsWith('/boards/')) {
    return (
      <a className={className} href={href} style={style} {...props}>
        {children}
      </a>
    );
  }

  return (
    <NextLink className={className} href={href} style={style} {...props}>
      {children}
    </NextLink>
  );
};
